/**
 * PeopleRoom — Mission Control Phase D.
 *
 * Surfaces expert supply metrics, seeker demand metrics,
 * the full expert roster, and the active vetting queue.
 *
 * Data: GET /api/admin/ops/people  (polled every 60 s)
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Users, RefreshCw, CheckCircle2, Clock, ChevronRight } from "lucide-react";
import { API } from "@/App";
import { getAdminToken } from "@/lib/authStorage";

// ── Auth helper ───────────────────────────────────────────────────────────────

function authHeaders() {
  const token = getAdminToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// ── Age helper ────────────────────────────────────────────────────────────────

function formatAge(hours) {
  if (hours == null) return "—";
  if (hours < 1) return "<1h ago";
  if (hours < 24) return `${Math.floor(hours)}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

// ── Month+Year helper ─────────────────────────────────────────────────────────

function formatSince(isoString) {
  if (!isoString) return "—";
  return new Date(isoString).toLocaleDateString("en-US", {
    month: "short",
    year: "numeric",
  });
}

// ── Inline: Stat card ─────────────────────────────────────────────────────────

function StatCard({ value, label, sublabel, color }) {
  const borderLeft =
    color === "green"
      ? "3px solid rgba(23,128,61,0.6)"
      : "3px solid transparent";

  return (
    <div
      style={{
        background: "var(--mc-surface)",
        border: "1px solid var(--mc-border)",
        borderLeft,
        borderRadius: 6,
        padding: "18px 20px 16px",
        minWidth: 0,
        flex: "1 1 0%",
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          fontFamily: "var(--font-display)",
          fontSize: 44,
          fontWeight: 400,
          lineHeight: 1,
          color: "var(--mc-text)",
          marginBottom: 6,
        }}
      >
        {value}
      </div>
      <div
        style={{
          fontFamily: "var(--font-sans)",
          fontSize: 13,
          color: "var(--color-slate-400)",
          lineHeight: 1.3,
        }}
      >
        {label}
      </div>
      {sublabel && (
        <div
          style={{
            fontFamily: "var(--font-sans)",
            fontSize: 11,
            color: "var(--color-slate-500)",
            marginTop: 3,
          }}
        >
          {sublabel}
        </div>
      )}
    </div>
  );
}

// ── Inline: Ghost stat card (loading skeleton) ────────────────────────────────

function GhostStatCard() {
  return (
    <div
      className="mc-skeleton-shimmer"
      style={{
        background: "var(--mc-surface)",
        border: "1px solid var(--mc-border)",
        borderRadius: 6,
        padding: "18px 20px 16px",
        flex: "1 1 0%",
        minHeight: 96,
        boxSizing: "border-box",
        animation: "mc-skeleton-shimmer 1.4s ease-in-out infinite",
      }}
      aria-hidden="true"
    />
  );
}

// ── Inline: Section header ────────────────────────────────────────────────────

function SectionHeader({ title, count }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 6,
        }}
      >
        <span
          className="mc-mono"
          style={{
            fontSize: 10,
            color: "var(--color-slate-400)",
            textTransform: "uppercase",
            letterSpacing: "0.1em",
          }}
        >
          {title}
        </span>
        {count != null && (
          <span
            style={{
              fontSize: 11,
              color: "var(--color-slate-500)",
              fontFamily: "var(--font-sans)",
            }}
          >
            {count}
          </span>
        )}
      </div>
      <div
        style={{
          height: 1,
          background: "var(--mc-border)",
          width: "100%",
        }}
      />
    </div>
  );
}

// ── Inline: Vetting stage badge ───────────────────────────────────────────────

function StageBadge({ stage }) {
  let chipClass = "mc-chip mc-chip--neutral";
  if (stage === "pending_human") chipClass = "mc-chip mc-chip--amber";
  else if (stage === "in_progress") chipClass = "mc-chip mc-chip--green";

  const label = stage ? stage.replace(/_/g, " ") : "—";

  return <span className={chipClass}>{label}</span>;
}

// ── Main component ────────────────────────────────────────────────────────────

export default function PeopleRoom() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [lastFetched, setLastFetched] = useState(null);

  // ── Fetch ───────────────────────────────────────────────────────────────────

  const fetchData = useCallback(async (isManual = false) => {
    if (isManual) setRefreshing(true);
    try {
      const res = await axios.get(`${API}/admin/ops/people`, {
        headers: authHeaders(),
      });
      setData(res.data);
      setError(null);
      setLastFetched(new Date());
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          err?.message ||
          "Failed to load people data."
      );
    } finally {
      setLoading(false);
      if (isManual) setRefreshing(false);
    }
  }, []);

  // Initial fetch + 60-second poll
  useEffect(() => {
    fetchData();
    const interval = setInterval(() => fetchData(), 60_000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // ── Derived ─────────────────────────────────────────────────────────────────

  const experts = data?.experts ?? {};
  const seekers = data?.seekers ?? {};
  const vettingQueue = data?.vetting_queue ?? [];
  const expertRoster = data?.expert_roster ?? [];

  const activeRoster = expertRoster.filter((e) => e.is_active);
  const displayedRoster = activeRoster.slice(0, 10);
  const overflowCount = activeRoster.length - displayedRoster.length;

  // ── Loading state ───────────────────────────────────────────────────────────

  if (loading) {
    return (
      <section className="mc-room mc-room--people" aria-label="People room">
        <div className="mc-room-header">
          <div>
            <div className="mc-room-eyebrow">ROOM · PEOPLE</div>
            <h1 className="mc-room-title">People</h1>
          </div>
        </div>

        <div className="mc-section">
          <SectionHeader title="Supply" />
          <div style={{ display: "flex", gap: 12 }}>
            <GhostStatCard />
            <GhostStatCard />
            <GhostStatCard />
          </div>
        </div>

        <div className="mc-section">
          <SectionHeader title="Demand" />
          <div style={{ display: "flex", gap: 12 }}>
            <GhostStatCard />
            <GhostStatCard />
            <GhostStatCard />
          </div>
        </div>

        <div
          style={{
            fontFamily: "var(--font-sans)",
            fontSize: 14,
            color: "var(--color-slate-500)",
            marginTop: 24,
          }}
        >
          Loading roster...
        </div>
      </section>
    );
  }

  // ── Error state (no cached data) ────────────────────────────────────────────

  if (error && !data) {
    return (
      <section className="mc-room mc-room--people" aria-label="People room">
        <div className="mc-room-header">
          <div>
            <div className="mc-room-eyebrow">ROOM · PEOPLE</div>
            <h1 className="mc-room-title">People</h1>
          </div>
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: 12,
            padding: "24px 0",
          }}
        >
          <p
            style={{
              fontFamily: "var(--font-sans)",
              fontSize: 14,
              color: "var(--color-critical)",
              margin: 0,
            }}
          >
            {error}
          </p>
          <button
            onClick={() => fetchData(true)}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "6px 14px",
              border: "1px solid var(--mc-border)",
              borderRadius: 6,
              background: "transparent",
              color: "var(--mc-text)",
              fontFamily: "var(--font-sans)",
              fontSize: 13,
              cursor: "pointer",
            }}
          >
            <RefreshCw size={13} />
            Retry
          </button>
        </div>
      </section>
    );
  }

  // ── Nominal render ──────────────────────────────────────────────────────────

  return (
    <section className="mc-room mc-room--people" aria-label="People room">
      {/* Room header */}
      <div className="mc-room-header">
        <div>
          <div className="mc-room-eyebrow">ROOM · PEOPLE</div>
          <h1 className="mc-room-title">People</h1>
        </div>
        <div
          style={{ display: "flex", alignItems: "center", gap: 12 }}
        >
          {lastFetched && (
            <span className="mc-room-meta">
              updated{" "}
              {lastFetched.toLocaleTimeString("en-US", {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </span>
          )}
          <button
            onClick={() => fetchData(true)}
            disabled={refreshing}
            aria-label="Refresh people data"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              padding: "5px 11px",
              border: "1px solid var(--mc-border)",
              borderRadius: 6,
              background: "transparent",
              color: refreshing
                ? "var(--color-slate-600)"
                : "var(--color-slate-400)",
              fontFamily: "var(--font-sans)",
              fontSize: 12,
              cursor: refreshing ? "default" : "pointer",
              transition: "color 150ms",
            }}
          >
            <RefreshCw
              size={12}
              style={{
                animation: refreshing ? "mc-dot-orbit 1s linear infinite" : "none",
              }}
            />
            {refreshing ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </div>

      {/* Stale error banner */}
      {error && data && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "8px 12px",
            background: "rgba(140,31,18,0.08)",
            border: "1px solid rgba(140,31,18,0.25)",
            borderRadius: 6,
            marginBottom: 20,
            fontFamily: "var(--font-sans)",
            fontSize: 12,
            color: "var(--color-critical)",
          }}
        >
          {error} — showing cached data.
        </div>
      )}

      {/* ── SUPPLY ──────────────────────────────────────────────────────────── */}
      <div className="mc-section">
        <SectionHeader
          title="Supply"
          count={`${experts.total ?? 0} total experts`}
        />
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <StatCard
            value={experts.active ?? 0}
            label="Active Experts"
            color="green"
          />
          <StatCard
            value={experts.vetting ?? 0}
            label="Vetting Queue"
            sublabel="pending review"
          />
          <StatCard
            value={experts.sourced ?? 0}
            label="Sourced Candidates"
            sublabel="not yet applied"
          />
        </div>
      </div>

      {/* ── DEMAND ──────────────────────────────────────────────────────────── */}
      <div className="mc-section">
        <SectionHeader
          title="Demand"
          count={`${seekers.total ?? 0} total seekers`}
        />
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <StatCard
            value={seekers.total ?? 0}
            label="Total Seekers"
          />
          <StatCard
            value={seekers.active_briefs ?? 0}
            label="Active Briefs"
            color="green"
          />
          <StatCard
            value={seekers.returning_30d ?? 0}
            label="Returning (30d)"
            sublabel="repeat visitors"
          />
        </div>
      </div>

      {/* ── EXPERT ROSTER ───────────────────────────────────────────────────── */}
      <div className="mc-section">
        <SectionHeader
          title="Expert Roster"
          count={`${activeRoster.length} active`}
        />

        {displayedRoster.length === 0 ? (
          <p
            style={{
              fontFamily: "var(--font-sans)",
              fontSize: 14,
              color: "var(--color-slate-500)",
              textAlign: "center",
              padding: "24px 0",
              margin: 0,
            }}
          >
            No active experts found.
          </p>
        ) : (
          <>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                tableLayout: "fixed",
              }}
            >
              <colgroup>
                <col style={{ width: "38%" }} />
                <col style={{ width: "22%" }} />
                <col style={{ width: "18%" }} />
                <col style={{ width: "22%" }} />
              </colgroup>
              <thead>
                <tr>
                  {["Name", "Status", "Verified", "Since"].map((h) => (
                    <th
                      key={h}
                      className="mc-mono"
                      style={{
                        fontSize: 11,
                        textTransform: "uppercase",
                        color: "var(--color-slate-400)",
                        letterSpacing: "0.08em",
                        fontWeight: 400,
                        textAlign: "left",
                        padding: "0 0 8px",
                        borderBottom: "1px solid var(--mc-border)",
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {displayedRoster.map((expert) => (
                  <tr
                    key={expert.id}
                    style={{ cursor: "default" }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background =
                        "rgba(255,255,255,0.03)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "transparent";
                    }}
                  >
                    {/* Name */}
                    <td
                      style={{
                        fontFamily: "var(--font-sans)",
                        fontSize: 13,
                        fontWeight: 500,
                        color: "var(--mc-text)",
                        padding: "8px 0",
                        borderBottom: "1px solid var(--mc-border)",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        paddingRight: 8,
                      }}
                    >
                      {expert.name ?? "—"}
                    </td>

                    {/* Status */}
                    <td
                      style={{
                        fontFamily: "var(--font-sans)",
                        fontSize: 13,
                        padding: "8px 0",
                        borderBottom: "1px solid var(--mc-border)",
                      }}
                    >
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                        }}
                      >
                        <span
                          style={{
                            width: 6,
                            height: 6,
                            borderRadius: "50%",
                            flexShrink: 0,
                            background: expert.is_active
                              ? "var(--color-green)"
                              : "var(--color-slate-500)",
                            display: "inline-block",
                          }}
                        />
                        <span
                          style={{
                            color: expert.is_active
                              ? "var(--color-green)"
                              : "var(--color-slate-500)",
                            fontSize: 12,
                          }}
                        >
                          {expert.is_active ? "active" : "inactive"}
                        </span>
                      </span>
                    </td>

                    {/* Verified */}
                    <td
                      style={{
                        fontFamily: "var(--font-sans)",
                        fontSize: 13,
                        padding: "8px 0",
                        borderBottom: "1px solid var(--mc-border)",
                      }}
                    >
                      {expert.verified ? (
                        <CheckCircle2
                          size={14}
                          style={{ color: "var(--color-green)" }}
                          aria-label="Verified"
                        />
                      ) : (
                        <span style={{ color: "var(--color-slate-600)" }}>
                          —
                        </span>
                      )}
                    </td>

                    {/* Since */}
                    <td
                      style={{
                        fontFamily: "var(--font-sans)",
                        fontSize: 13,
                        color: "var(--color-slate-500)",
                        padding: "8px 0",
                        borderBottom: "1px solid var(--mc-border)",
                      }}
                    >
                      {formatSince(expert.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {overflowCount > 0 && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  paddingTop: 10,
                  fontFamily: "var(--font-sans)",
                  fontSize: 12,
                  color: "var(--color-slate-500)",
                }}
              >
                <ChevronRight size={12} />
                and {overflowCount} more active expert
                {overflowCount !== 1 ? "s" : ""}
              </div>
            )}
          </>
        )}
      </div>

      {/* ── VETTING QUEUE ───────────────────────────────────────────────────── */}
      <div className="mc-section">
        <SectionHeader
          title="Vetting Queue"
          count={
            vettingQueue.length > 0
              ? `${vettingQueue.length} pending`
              : undefined
          }
        />

        {vettingQueue.length === 0 ? (
          <p
            style={{
              fontFamily: "var(--font-sans)",
              fontSize: 14,
              color: "var(--color-slate-500)",
              textAlign: "center",
              padding: "24px 0",
              margin: 0,
            }}
          >
            No applications pending review.
          </p>
        ) : (
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              tableLayout: "fixed",
            }}
          >
            <colgroup>
              <col style={{ width: "32%" }} />
              <col style={{ width: "38%" }} />
              <col style={{ width: "30%" }} />
            </colgroup>
            <thead>
              <tr>
                {["Application ID", "Stage", "Age"].map((h) => (
                  <th
                    key={h}
                    className="mc-mono"
                    style={{
                      fontSize: 11,
                      textTransform: "uppercase",
                      color: "var(--color-slate-400)",
                      letterSpacing: "0.08em",
                      fontWeight: 400,
                      textAlign: "left",
                      padding: "0 0 8px",
                      borderBottom: "1px solid var(--mc-border)",
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {vettingQueue.map((item) => {
                const shortId = item.id
                  ? item.id.replace(/-/g, "").slice(0, 8)
                  : "—";
                return (
                  <tr
                    key={item.id ?? item.expert_id}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background =
                        "rgba(255,255,255,0.03)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "transparent";
                    }}
                  >
                    {/* Application ID */}
                    <td
                      style={{
                        padding: "8px 0",
                        borderBottom: "1px solid var(--mc-border)",
                        paddingRight: 8,
                      }}
                    >
                      <span
                        className="mc-mono"
                        style={{
                          fontSize: 12,
                          color: "var(--color-slate-400)",
                          letterSpacing: "0.04em",
                        }}
                      >
                        {shortId}…
                      </span>
                    </td>

                    {/* Stage */}
                    <td
                      style={{
                        padding: "8px 0",
                        borderBottom: "1px solid var(--mc-border)",
                      }}
                    >
                      <StageBadge stage={item.status} />
                    </td>

                    {/* Age */}
                    <td
                      style={{
                        fontFamily: "var(--font-sans)",
                        fontSize: 13,
                        color: "var(--color-slate-500)",
                        padding: "8px 0",
                        borderBottom: "1px solid var(--mc-border)",
                      }}
                    >
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 4,
                        }}
                      >
                        <Clock
                          size={11}
                          style={{
                            color: "var(--color-slate-600)",
                            flexShrink: 0,
                          }}
                        />
                        {formatAge(item.age_hours)}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}
